/**
 * La lista de clientes.
 *
 * EL ORDEN NO ES ALFABETICO
 * -------------------------
 * Los que deben van primero, del que mas debe al que menos. Una lista de
 * clientes ordenada por nombre contesta "¿como se llamaba?", que se contesta
 * mejor con el buscador. Ordenada por deuda contesta "¿a quien tengo que
 * llamar hoy?", que es la unica pregunta por la que alguien abre esta pantalla
 * sin tener un nombre en la cabeza.
 *
 * Cada saldo va en LA MONEDA DE SU CUENTA. No se convierte todo a pesos para
 * que la columna quede prolija: dos clientes pueden deber numeros parecidos en
 * monedas distintas y eso tiene que verse.
 */
import type { KeyboardEvent, ReactNode } from "react";
import { TriangleAlert } from "lucide-react";

import { BadgeMora } from "@/components/cobro/BadgeMora";
import { ChipMoneda, Importe } from "@/components/money";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { EstadoVacio } from "@/components/ui/estado-vacio";
import { InputBusqueda } from "@/components/ui/input-busqueda";
import { SkeletonTabla, SkeletonTarjetas } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableCellImporte,
  TableHead,
  TableHeader,
  TableRow,
  TableVacia,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

import { totalizarDeuda, type FilaLista } from "./logica";

const COLUMNAS = 5;

export interface ListaClientesProps {
  filas: ReadonlyArray<FilaLista>;
  seleccionadoId: number | null;
  onSeleccionar: (id: number) => void;
  texto: string;
  onTexto: (t: string) => void;
  cargando?: boolean;
  /**
   * Cuantos clientes hay EN LA BASE con la busqueda actual, no cuantos se
   * trajeron. Antes esto era `filas.length` del que llamaba, asi que el
   * contador comparaba la pagina contra si misma y siempre cerraba.
   */
  total: number;
  /** El `PieDeLista`: cuantos se muestran de cuantos hay, y «Ver más». */
  pie?: ReactNode;
}

export function ListaClientes({
  filas,
  seleccionadoId,
  onSeleccionar,
  texto,
  onTexto,
  cargando = false,
  total,
  pie,
}: ListaClientesProps): JSX.Element {
  const vacio = filas.length === 0;
  // Skeleton solo en la primera carga (06 §4.2); con datos en pantalla, un
  // refetch atenua la lista y nunca la vacia.
  const primeraCarga = cargando && vacio;
  const actualizando = cargando && !vacio;
  const totales = totalizarDeuda(filas);
  const mensajeVacio = texto.trim()
    ? `No encontré a «${texto.trim()}».`
    : "Todavía no hay clientes cargados.";

  return (
    /*
      Columna de alto completo: el buscador y el contador arriba, y la tabla se
      lleva todo lo que sobra. La pantalla no scrollea —scrollea la tabla— asi
      que el encabezado sticky y el buscador quedan siempre a la vista.
    */
    <section
      aria-busy={actualizando || undefined}
      className={cn(
        "flex h-full flex-col gap-3 transition-opacity duration-150",
        actualizando && "opacity-60",
      )}
    >
      <InputBusqueda
        valor={texto}
        onValorChange={onTexto}
        etiqueta="Buscar cliente"
        placeholder="Buscar por nombre, teléfono o documento"
        cargando={cargando && !vacio}
        className="shrink-0"
        /*
          Enter con un solo resultado abre esa ficha. Buscar a alguien por el
          nombre y despues tener que ir a tocarlo con el mouse es un paso de mas
          cuando ya no quedo ninguna duda de a quien se buscaba.
        */
        onKeyDown={(e) => {
          const unica = filas.length === 1 ? filas[0] : undefined;
          if (e.key !== "Enter" || !unica) return;
          e.preventDefault();
          onSeleccionar(unica.cliente.id);
        }}
      />

      <div className="flex shrink-0 flex-wrap items-baseline justify-between gap-x-3 gap-y-1 text-xs text-muted-foreground">
        <span>
          {filas.length} de {total} clientes
        </span>
        {/*
          Dos totales y no uno. Los pesos de las cuentas fijas y los dolares de
          las ancladas no se suman: seria un numero que no le corresponde a nadie.
        */}
        {totales.cuentasArs > 0 || totales.cuentasUsd > 0 ? (
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
            {totales.cuentasArs > 0 ? (
              <span className="inline-flex items-center gap-1">
                Deben <Importe monto={totales.ars} moneda="ARS" tamano="sm" /> en{" "}
                {totales.cuentasArs} {totales.cuentasArs === 1 ? "cuenta" : "cuentas"} en pesos
              </span>
            ) : null}
            {totales.cuentasUsd > 0 ? (
              <span className="inline-flex items-center gap-1">
                y <Importe monto={totales.usd} moneda="USD" tamano="sm" /> en {totales.cuentasUsd}{" "}
                {totales.cuentasUsd === 1 ? "cuenta" : "cuentas"} en dólares
              </span>
            ) : null}
          </span>
        ) : null}
      </div>

      {/* Desktop: tabla densa. La fila navega (Enter o click) y el nombre es
          texto: un boton adentro de una fila clickeable es doble objetivo. */}
      <Card padding="none" className="hidden min-h-0 flex-1 overflow-hidden md:block">
        {primeraCarga ? (
          <SkeletonTabla columnas={COLUMNAS} densidad="compacta" />
        ) : (
          <Table densidad="compacta" alto="lleno">
            <TableHeader>
              <TableRow>
                <TableHead>Cliente</TableHead>
                <TableHead>Teléfono</TableHead>
                <TableHead>Cuenta</TableHead>
                <TableHead className="text-right">Saldo</TableHead>
                <TableHead>Estado</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {vacio ? (
                <TableVacia colSpan={COLUMNAS} mensaje={mensajeVacio} />
              ) : (
                filas.map((f) => (
                  <FilaCliente
                    key={f.cliente.id}
                    fila={f}
                    seleccionada={f.cliente.id === seleccionadoId}
                    onSeleccionar={onSeleccionar}
                  />
                ))
              )}
              {!vacio && pie ? (
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={COLUMNAS}>{pie}</TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        )}
      </Card>

      {/* Mobile: una tarjeta-registro por cliente, tocable entera. */}
      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto md:hidden">
        {primeraCarga ? (
          <SkeletonTarjetas />
        ) : vacio ? (
          <EstadoVacio titulo={mensajeVacio} />
        ) : (
          <>
            {filas.map((f) => (
              <TarjetaCliente
                key={f.cliente.id}
                fila={f}
                seleccionada={f.cliente.id === seleccionadoId}
                onSeleccionar={onSeleccionar}
              />
            ))}
            {pie}
          </>
        )}
      </div>
    </section>
  );
}

interface FilaProps {
  fila: FilaLista;
  seleccionada: boolean;
  onSeleccionar: (id: number) => void;
}

function FilaCliente({ fila: f, seleccionada, onSeleccionar }: FilaProps): JSX.Element {
  function alTeclado(e: KeyboardEvent<HTMLTableRowElement>): void {
    if (e.key !== "Enter" && e.key !== " ") return;
    e.preventDefault();
    onSeleccionar(f.cliente.id);
  }

  return (
    <TableRow
      data-state={seleccionada ? "selected" : undefined}
      tabIndex={0}
      onClick={() => onSeleccionar(f.cliente.id)}
      onKeyDown={alTeclado}
      className="cursor-pointer focus-visible:bg-accent focus-visible:outline-none"
    >
      <TableCell className="text-sm font-medium">{f.cliente.nombre}</TableCell>
      <TableCell className="whitespace-nowrap text-sm tabular-nums text-muted-foreground">
        {f.cliente.telefono ?? "—"}
      </TableCell>
      <TableCell>
        <ChipCuenta moneda={f.moneda} />
      </TableCell>
      <TableCellImporte>
        <SaldoDeFila fila={f} />
      </TableCellImporte>
      <TableCell>
        <EstadoDeFila fila={f} />
      </TableCell>
    </TableRow>
  );
}

/**
 * Tarjeta-registro (06 seccion 3.4): nombre y estado a la izquierda, el saldo
 * arriba a la derecha, barra lateral con la moneda de la cuenta.
 *
 * `<button>` crudo: toda la tarjeta es el objetivo y `Card` no admite
 * `as="button"`; el ring va adentro (`ring-inset`) para que el
 * `overflow-hidden` de la tarjeta no lo recorte.
 */
function TarjetaCliente({ fila: f, seleccionada, onSeleccionar }: FilaProps): JSX.Element {
  return (
    // `shrink-0`: la lista es una columna flex de alto fijo y, con
    // `overflow-hidden`, la tarjeta se dejaba achicar hasta 5 px para entrar
    // (Clientes › Todos en el celular, 31 rayas sin nombre).
    <Card as="article" padding="none" acento={f.moneda} className="shrink-0 overflow-hidden">
      {/* <button> crudo: tarjeta-registro clickeable entera; el ring va adentro. */}
      <button
        type="button"
        onClick={() => onSeleccionar(f.cliente.id)}
        aria-current={seleccionada ? "true" : undefined}
        className={cn(
          "flex w-full items-start justify-between gap-3 px-4 py-3 text-left transition-colors duration-150 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
          seleccionada && "bg-accent",
        )}
      >
        <div className="min-w-0 space-y-1">
          <span className="block break-words text-sm font-medium sm:truncate">
            {f.cliente.nombre}
          </span>
          <span className="block truncate text-xs tabular-nums text-muted-foreground">
            {f.cliente.telefono ?? "sin teléfono"}
          </span>
          <EstadoDeFila fila={f} />
        </div>
        <div className="shrink-0 text-right">
          <SaldoDeFila fila={f} />
        </div>
      </button>
    </Card>
  );
}

function ChipCuenta({ moneda }: { moneda: "ARS" | "USD" }): JSX.Element {
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs text-muted-foreground">
      <ChipMoneda moneda={moneda} />
      {moneda === "USD" ? "sigue al dólar" : "fija"}
    </span>
  );
}

/**
 * El saldo de la fila, en la moneda de la cuenta.
 *
 * Si la cuenta esta anclada al dolar se muestra ademas, chiquito, lo que hay que
 * cobrarle hoy en pesos. Los dos numeros son ciertos; el de arriba es la deuda y
 * el de abajo es como se cobra hoy. "A favor" es un badge al lado del numero,
 * que sigue en el color de su moneda.
 */
function SaldoDeFila({ fila }: { fila: FilaLista }): JSX.Element {
  if (fila.monto > 0.009) {
    return (
      <span className="inline-flex flex-col items-end gap-0.5">
        <Importe monto={fila.monto} moneda={fila.moneda} tamano="sm" />
        {fila.moneda === "USD" ? (
          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
            hoy <Importe monto={fila.deudaArsHoy} moneda="ARS" tamano="sm" codigo={false} />
          </span>
        ) : null}
      </span>
    );
  }
  if (fila.monto < -0.009) {
    return (
      <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
        <Badge variant="success">A favor</Badge>
        <Importe monto={Math.abs(fila.monto)} moneda={fila.moneda} tamano="sm" />
      </span>
    );
  }
  return <span className="text-xs text-muted-foreground">sin deuda</span>;
}

function EstadoDeFila({ fila }: { fila: FilaLista }): JSX.Element {
  return (
    <span className="flex flex-wrap items-center gap-1.5">
      <BadgeMora estado={fila.mora} />
      {fila.excedido ? (
        <Badge variant="destructive">
          <TriangleAlert aria-hidden="true" />
          excedió el límite
        </Badge>
      ) : null}
    </span>
  );
}
